// Centralized insights data for the ITDukes Insights hub

export interface InsightMeta {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  tag: string;
  icon: string; // lucide icon name
  publishedDate: string;
  lastModified?: string; // ISO date of last meaningful content update
  readTime: string;
  stats: {
    value: string;
    label: string;
  }[];
  featured?: boolean;
  isNew?: boolean; // Deprecated: use isRecentlyPublished() helper instead
  comingSoon?: boolean;
}

// Helper: insight is "new" if published within the last 60 days
export const isRecentlyPublished = (insight: InsightMeta): boolean => {
  const sixtyDaysAgo = new Date();
  sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
  return new Date(insight.publishedDate) >= sixtyDaysAgo;
};

export const getInsightModifiedDate = (insight: InsightMeta): string =>
  insight.lastModified ?? insight.publishedDate;

export const insights: InsightMeta[] = [
  {
    slug: "per-seat-pricing-token-economics",
    title: "Per Seat Is Dying: How Token Pricing Is Rewriting SaaS Budgets",
    subtitle:
      "For twenty years software cost the same every month. AI turned it into a utility bill.",
    description:
      "Per seat SaaS pricing gave finance teams predictable bills. AI features are priced on tokens, credits and outcomes, and vendors from Salesforce to Cursor to Zendesk have moved to usage models. Here is why it is happening, what it does to budgets, and how buyers should respond.",
    tag: "AI Development",
    icon: "Receipt",
    publishedDate: "2026-08-17",
    readTime: "11 min read",
    featured: true,
    stats: [
      { value: "20 years", label: "per seat was the SaaS default" },
      { value: "3 models", label: "tokens, credits, outcomes" },
      { value: "Unbounded", label: "an AI line item with no cap" },
    ],
  },
  {
    slug: "email-blacklists-explained",
    title: "Email Blacklists Explained: Spamhaus, SURBL, and What Actually Affects Deliverability",
    subtitle: "Which listings matter and which ones nobody reads",
    description:
      "Most senders misunderstand what blacklists are, who actually queries them, and which listings affect inbox placement. A clear breakdown of sender based versus content based lists, how domains get listed, and how to judge severity.",
    tag: "Email Deliverability",
    icon: "ShieldAlert",
    publishedDate: "2026-07-30",
    readTime: "9 min read",
    stats: [
      { value: "100+", label: "public blacklists" },
      { value: "2", label: "that truly matter" },
      { value: "0", label: "used directly by Gmail" },
    ],
  },
  {
    slug: "december-email-drop",
    title: "Why December Email Replies Drop",
    subtitle: "Seasonality versus Deliverability",
    description: "Industry data shows December conversion rates drop from 6.1% to 2.9% while email volume surges 30%+. Learn why this is seasonal, not a deliverability failure.",
    tag: "Seasonal Analysis",
    icon: "Calendar",
    publishedDate: "2025-12-05",
    lastModified: "2026-08-17",
    readTime: "8 min read",
    stats: [
      { value: "84%", label: "global inbox rate" },
      { value: "6.1%→2.9%", label: "conversion drop" },
    ],
    featured: true,
  },
  {
    slug: "sending-time-spam-filters",
    title: "Why Sending Time Patterns Trigger Spam Filters",
    subtitle: "Bots send at perfect intervals, humans don't",
    description: "Research shows 30-40% of email opens are from security bots. Learn how AI-driven filters detect automation and what the 0.3% spam threshold means for your campaigns.",
    tag: "Deliverability",
    icon: "Clock",
    publishedDate: "2025-12-07",
    readTime: "7 min read",
    stats: [
      { value: "84%", label: "global inbox rate" },
      { value: "0.3%", label: "spam threshold" },
    ],
    isNew: true,
  },
  {
    slug: "domain-warmup-strategies",
    title: "Email Domain Warmup Strategies That Actually Work",
    subtitle: "Build reputation before you scale",
    description: "A step-by-step guide to warming up new email domains, including volume schedules, engagement tactics, and common mistakes to avoid.",
    tag: "Domain Health",
    icon: "Flame",
    publishedDate: "2025-12-09",
    readTime: "10 min read",
    stats: [
      { value: "4-8 weeks", label: "warmup period" },
      { value: "95%+", label: "target inbox rate" },
    ],
    isNew: true,
  },
  {
    slug: "spf-dkim-dmarc-guide",
    title: "SPF, DKIM & DMARC: The Complete Technical Guide",
    subtitle: "Authentication protocols demystified",
    description: "Deep dive into email authentication protocols. Learn how to properly configure SPF, DKIM, and DMARC records to maximize deliverability.",
    tag: "Technical",
    icon: "Shield",
    publishedDate: "2025-12-11",
    lastModified: "2026-08-17",
    readTime: "12 min read",
    stats: [
      { value: "3 protocols", label: "explained" },
      { value: "100%", label: "auth rate target" },
    ],
    isNew: true,
  },
  {
    slug: "cold-email-deliverability",
    title: "Cold Email Deliverability: What the Data Shows",
    subtitle: "Outreach that actually reaches inboxes",
    description: "Analysis of cold email performance metrics, including inbox placement rates, optimal sending volumes, and content patterns that avoid spam filters.",
    tag: "Cold Outreach",
    icon: "Send",
    publishedDate: "2025-12-17",
    readTime: "9 min read",
    stats: [
      { value: "50-100", label: "daily limit" },
      { value: "65%", label: "avg open rate" },
    ],
    isNew: true,
  },
  {
    slug: "gmail-promotions-tab",
    title: "Understanding Gmail's Promotions Tab Algorithm",
    subtitle: "Why you land there and what to do about it",
    description: "Research into how Gmail categorizes emails into Primary vs Promotions tabs, and strategies to improve your chances of landing in Primary.",
    tag: "Gmail",
    icon: "Inbox",
    publishedDate: "2025-12-13",
    lastModified: "2026-08-17",
    readTime: "7 min read",
    stats: [
      { value: "68.8%", label: "check Promotions" },
      { value: "19.2%", label: "Promos open rate" },
    ],
    isNew: true,
  },
  {
    slug: "phishing-attack-detection",
    title: "How to Identify and Prevent Phishing Attacks",
    subtitle: "Protect your organization from social engineering",
    description: "Learn to recognize phishing emails, understand attack vectors, and implement security measures that protect your team from credential theft and data breaches.",
    tag: "Cybersecurity",
    icon: "ShieldAlert",
    publishedDate: "2025-12-19",
    readTime: "11 min read",
    stats: [
      { value: "91%", label: "attacks via email" },
      { value: "$4.76M", label: "avg breach cost" },
    ],
    isNew: true,
  },
  {
    slug: "secure-coding-practices",
    title: "Secure Coding Practices for Web Developers",
    subtitle: "Build security into your development workflow",
    description: "Essential security practices every developer should follow: input validation, authentication best practices, OWASP Top 10, and secure API design patterns.",
    tag: "Development",
    icon: "Code",
    publishedDate: "2025-12-21",
    readTime: "14 min read",
    stats: [
      { value: "70%", label: "vulns preventable" },
      { value: "OWASP 10", label: "covered" },
    ],
    isNew: true,
  },
  {
    slug: "api-security-best-practices",
    title: "API Security: Authentication, Rate Limiting & Beyond",
    subtitle: "Secure your endpoints from common attacks",
    description: "Comprehensive guide to securing REST and GraphQL APIs including JWT best practices, rate limiting strategies, input sanitization, and preventing injection attacks.",
    tag: "Development",
    icon: "Lock",
    publishedDate: "2025-12-23",
    readTime: "12 min read",
    stats: [
      { value: "94%", label: "apps have API vulns" },
      { value: "3x", label: "attacks increased" },
    ],
    isNew: true,
  },
  {
    slug: "vibe-coding-guide",
    title: "Vibe Coding: The Future of AI-Assisted Development",
    subtitle: "Code with natural language, ship faster",
    description: "Learn how vibe coding is revolutionizing software development. Understand prompt-driven development, AI pair programming, and how to 10x your productivity with tools like Cursor, Bolt, and Claude.",
    tag: "AI Development",
    icon: "Sparkles",
    publishedDate: "2025-12-25",
    readTime: "10 min read",
    stats: [
      { value: "10x", label: "faster shipping" },
      { value: "70%", label: "less boilerplate" },
    ],
    isNew: true,
  },
  {
    slug: "ai-coding-assistants",
    title: "AI Coding Assistants: Complete Comparison Guide",
    subtitle: "GitHub Copilot vs Cursor vs Claude vs ChatGPT",
    description: "In-depth comparison of the best AI coding tools in 2026. Features, pricing, strengths, and which one to choose for your workflow.",
    tag: "AI Development",
    icon: "Bot",
    publishedDate: "2025-12-27",
    lastModified: "2026-08-17",
    readTime: "12 min read",
    stats: [
      { value: "92%", label: "devs use AI tools" },
      { value: "55%", label: "productivity boost" },
    ],
    isNew: true,
  },
  {
    slug: "prompt-engineering-developers",
    title: "Prompt Engineering for Developers",
    subtitle: "Write better prompts, get better code",
    description: "Master the art of prompting AI coding assistants. Learn techniques for code generation, debugging, refactoring, and building complex features with AI.",
    tag: "AI Development",
    icon: "MessageSquareCode",
    publishedDate: "2025-12-28",
    readTime: "14 min read",
    stats: [
      { value: "3x", label: "better outputs" },
      { value: "80%", label: "fewer iterations" },
    ],
    isNew: true,
  },
  {
    slug: "no-code-vs-code",
    title: "No-Code vs Traditional Development: When to Use What",
    subtitle: "Build faster without sacrificing quality",
    description: "Understand when to use no-code platforms vs custom development. Learn hybrid approaches that combine speed with flexibility for startups and enterprises.",
    tag: "Development",
    icon: "Blocks",
    publishedDate: "2025-12-29",
    readTime: "9 min read",
    stats: [
      { value: "65%", label: "faster MVPs" },
      { value: "$50K+", label: "avg savings" },
    ],
    isNew: true,
  },
  {
    slug: "ai-agents-automation",
    title: "Building AI Agents for Business Automation",
    subtitle: "Autonomous workflows that actually work",
    description: "Practical guide to building AI agents that automate repetitive tasks. From simple chatbots to complex multi-step workflows with tools like n8n, Make, and custom solutions.",
    tag: "AI Development",
    icon: "Workflow",
    publishedDate: "2025-12-30",
    readTime: "13 min read",
    stats: [
      { value: "40hrs", label: "saved per week" },
      { value: "85%", label: "task automation" },
    ],
    isNew: true,
  },
  {
    slug: "llm-integration-guide",
    title: "Integrating LLMs Into Your Applications",
    subtitle: "From API calls to production-ready features",
    description: "Complete guide to adding AI capabilities to your apps. Covers API integration, prompt management, streaming responses, error handling, and cost optimization.",
    tag: "AI Development",
    icon: "Cpu",
    publishedDate: "2025-12-31",
    readTime: "15 min read",
    stats: [
      { value: "OpenAI", label: "+ alternatives" },
      { value: "90%", label: "cost reduction tips" },
    ],
    isNew: true,
  },
  {
    slug: "project-management-tools",
    title: "Project Management Tools: Complete Comparison Guide",
    subtitle: "Lottae vs Asana vs Monday vs Trello vs Jira",
    description: "In-depth comparison of the best project management tools in 2026. See why Lottae offers the best pricing at $3.50/user with unlimited features, white-labeling, and no tier restrictions.",
    tag: "Productivity",
    icon: "FolderKanban",
    publishedDate: "2025-12-15",
    lastModified: "2026-08-17",
    readTime: "11 min read",
    stats: [
      { value: "7+", label: "tools compared" },
      { value: "$3.50", label: "lowest per-user" },
    ],
    isNew: true,
  },
  {
    slug: "reply-based-deliverability",
    title: "The End of the Open Rate: Reply-Based Deliverability",
    subtitle: "Why ISPs value replies more than opens in 2026",
    description: "Open rates are dead. In 2026, major ISPs prioritize reply signals over opens when determining inbox placement. Learn how to transition your email strategy from opens to replies.",
    tag: "Deliverability",
    icon: "MessageSquareReply",
    publishedDate: "2026-01-27",
    readTime: "11 min read",
    stats: [
      { value: "Reply", label: "is the new open" },
      { value: "2026", label: "ISP shift complete" },
    ],
    isNew: true,
  },
  {
    slug: "zero-trust-email-dmarc",
    title: "Zero-Trust Email: Why DMARC is No Longer Optional",
    subtitle: "Google and Yahoo's Strict Alignment model explained",
    description: "In 2026, Google and Yahoo require strict DMARC alignment for all senders. This technical deep-dive explains the new zero-trust email paradigm and how to achieve compliance.",
    tag: "Technical",
    icon: "ShieldCheck",
    publishedDate: "2026-01-27",
    readTime: "14 min read",
    stats: [
      { value: "Strict", label: "alignment required" },
      { value: "p=reject", label: "new baseline" },
    ],
    isNew: true,
  },
  {
    slug: "geo-vs-seo",
    title: "GEO vs SEO: How to Rank in ChatGPT and Perplexity",
    subtitle: "Generative Engine Optimization for AI-first search",
    description: "Traditional SEO isn't enough anymore. Learn how Generative Engine Optimization (GEO) differs from SEO, and the tactics that get your content cited by ChatGPT, Perplexity, and Claude.",
    tag: "AI Development",
    icon: "Sparkles",
    publishedDate: "2026-01-27",
    readTime: "13 min read",
    stats: [
      { value: "GEO", label: "new ranking factor" },
      { value: "40%", label: "search via AI" },
    ],
    isNew: true,
    featured: true,
  },
  {
    slug: "it-stakeholder-roles",
    title: "IT Stakeholder Roles: Who Does What in Technology Projects",
    subtitle: "CTOs, CIOs, IT Managers, and DevOps explained",
    description: "Understand the key IT stakeholder roles in modern organizations. Learn the differences between CTOs, CIOs, IT Directors, DevOps Engineers, and how they collaborate on technology initiatives.",
    tag: "Business IT",
    icon: "Users",
    publishedDate: "2026-02-08",
    readTime: "10 min read",
    stats: [
      { value: "6+", label: "key roles explained" },
      { value: "RACI", label: "matrix included" },
    ],
    isNew: true,
  },
  {
    slug: "startup-failure-delegation",
    title: "Why Startups Fail: The Founder Delegation Crisis",
    subtitle: "90% of startups fail—and the inability to delegate is a leading cause",
    description: "Learn why founders struggle to let go, how delegation failures lead to burnout and team problems, and a framework to build a company that scales without you.",
    tag: "Business IT",
    icon: "AlertTriangle",
    publishedDate: "2026-02-09",
    readTime: "12 min read",
    stats: [
      { value: "90%", label: "startups fail" },
      { value: "23%", label: "fail from wrong team" },
    ],
    isNew: true,
    featured: true,
  },
  {
    slug: "departmental-autonomy",
    title: "Stop Skipping Levels: Why Leaders Must Respect the Chain of Command",
    subtitle: "The top-down hierarchy problem that destroys manager authority",
    description: "When CEOs message developers directly and executives bypass managers, organizations fall apart. Learn why top-down hierarchy violations are the real problem—and how to communicate properly through the chain.",
    tag: "Business IT",
    icon: "ArrowDown",
    publishedDate: "2026-02-09",
    readTime: "9 min read",
    stats: [
      { value: "67%", label: "managers feel bypassed" },
      { value: "45%", label: "higher turnover" },
    ],
    isNew: true,
  },
  {
    slug: "upwork-fiverr-decline",
    title: "The Upwork, Fiverr & Freelancer.com Collapse: Inside the Numbers",
    subtitle: "Three public marketplaces, three versions of the same downward chart",
    description: "Upwork lost ~47K active clients in a year (stock -56% YTD 2026). Fiverr's active buyers fell 13.6% YoY. Freelancer.com's parent (ASX: FLN) reported group GMV -7.1% with the stock down ~37% YTD. The data on why generative AI is eating the freelance marketplace model.",
    tag: "Business IT",
    icon: "TrendingDown",
    publishedDate: "2026-05-25",
    readTime: "13 min read",
    stats: [
      { value: "-47K", label: "Upwork clients lost YoY" },
      { value: "-13.6%", label: "Fiverr active buyers YoY" },
      { value: "-7.1%", label: "Freelancer.com group GMV YoY" },
    ],
    isNew: true,
    featured: true,
  },
  {
    slug: "microsoft-drops-claude-code",
    title: "Microsoft Drops Claude Code by June 30, 2026: Inside the AI Budget Blowout",
    subtitle: "~100,000 engineers cut off Claude Code after token costs ran past forecast",
    description: "Microsoft is cancelling most internal Claude Code licenses across its Experiences + Devices group (Windows, M365, Outlook, Teams, Surface) by June 30, 2026 — the end of its fiscal year — and pushing engineers to GitHub Copilot CLI. The Verge's Tom Warren broke the story on May 14, 2026: sources say the decision is partly about convergence on Copilot, and partly financial, after six months of runaway token-based usage. Here are the verified numbers, the business logic, and what it means for everyone else paying per-token for AI coding.",
    tag: "AI Development",
    icon: "Bot",
    publishedDate: "2026-05-26",
    readTime: "12 min read",
    stats: [
      { value: "~100K", label: "Microsoft engineers affected" },
      { value: "Jun 30, 2026", label: "Claude Code cutoff date" },
      { value: "6 months", label: "from rollout to rollback" },
    ],
    isNew: true,
    featured: true,
  },
  {
    slug: "ai-cost-vs-employees",
    title: "AI vs Employees: The 2026 Cost Math No One Wants to Admit",
    subtitle: "MIT says AI is cheaper than a human in only 23% of jobs — and tokens are a meter, not a salary",
    description: "A 2024 MIT CSAIL study found AI is cost-effective in only 23% of vision-exposed tasks. Nvidia's own AI lead told Axios compute now costs more than his team's salaries. Uber burned its 2026 AI coding budget in four months. Tech is committing ~$740B of AI capex in 2026 (Morgan Stanley). Here's what the verified numbers say about whether AI is actually replacing your team — or just sending you a bigger bill.",
    tag: "AI Development",
    icon: "DollarSign",
    publishedDate: "2026-06-04",
    readTime: "11 min read",
    stats: [
      { value: "23%", label: "of jobs where AI beats human cost (MIT)" },
      { value: "4 months", label: "for Uber to burn its 2026 AI budget" },
      { value: "$740B", label: "Big Tech AI capex committed in 2026" },
    ],
    isNew: true,
    featured: true,
  },
  {
    slug: "summer-email-drop",
    title: "Why Summer Email Engagement Drops",
    subtitle: "Vacation season, not deliverability failure",
    description: "From mid-June through late August, B2B reply rates and pipeline activity slow as decision makers across the US and Europe take leave. ZoomInfo data shows the summer out-of-office reply rate sits around 3% — modest, but enough to feel quieter. The dip is seasonal recipient behaviour, not a technical problem.",
    tag: "Seasonal Analysis",
    icon: "Sun",
    publishedDate: "2026-06-05",
    readTime: "8 min read",
    stats: [
      { value: "~3%", label: "OOO reply rate (Jun-Aug, ZoomInfo)" },
      { value: "Mid-Jun → late Aug", label: "peak holiday window" },
      { value: "Seasonal", label: "not a deliverability failure" },
    ],
    isNew: true,
    featured: true,
  },
];

export const getInsightBySlug = (slug: string) => insights.find(i => i.slug === slug);

export const getFeaturedInsight = () => insights.find(i => i.featured);

export const getLatestInsights = (count?: number) => {
  const sorted = [...insights].sort((a, b) => 
    new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
  );
  return count ? sorted.slice(0, count) : sorted;
};
